import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  forwardRef,
} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

import {
  DATE_RANGE_FILTER_OPTIONS,
  DateRangeFilter,
} from '../../interfaces/date-range-filter.interface';

/**
 * @component DateRangeFilterComponent
 *
 * @description
 * A reusable date range filtering component supporting predefined options (e.g. Today, Last Week),
 * custom ranges (Before, After, Between), and ControlValueAccessor (CVA) integration for reactive forms.
 *
 * Designed for flexible integration into dashboards, reporting filters, or data-driven forms.
 * Emits selected range via both Angular form API and a `valueChange` EventEmitter.
 *
 * Supports mobile-friendly rendering, min-date constraints, and custom dropdown options.
 *
 * @usageNotes
 * - Use within reactive forms with `[formControl]` or `formControlName`
 * - Bind to `(valueChange)` for output value changes
 * - Handles complex value types: `Date`, `[Date, Date]`, or `null`
 * - Pass custom filter options via `[filterOptions]` input
 *
 * @example
 * ```html
 * <lib-date-range-filter
 *   [label]="'Created Date'"
 *   [placeholder]="'Choose range...'"
 *   [minDate]="minSelectableDate"
 *   [filterOptions]="customFilterOptions"
 *   [formControl]="dateRangeControl"
 *   (valueChange)="onDateChange($event)">
 * </lib-date-range-filter>
 * ```
 */
@Component({
  selector: 'lib-date-range-filter',
  templateUrl: './date-range-filter.component.html',
  styleUrl: './date-range-filter.component.scss',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateRangeFilterComponent),
      multi: true,
    },
  ],
  standalone: false,
})
export class DateRangeFilterComponent implements OnInit, ControlValueAccessor {
  @Input() public showLabelOnMobile = true;

  // Label for the component
  @Input() public label = 'By Date';

  // Placeholder text shown in the UI
  @Input() public placeholder = 'Select Date';

  // Optional minimum selectable date
  @Input() public minDate: Date | null = null;

  // Controls mobile-friendly rendering
  @Input() public isMobile = false;

  // Custom filter options (optional)
  @Input() public optionList: Array<{ label: string; value: string }> = [];

  // Custom date range filter options (new input)
  @Input() public filterOptions: DateRangeFilter[] = DATE_RANGE_FILTER_OPTIONS;

  // Default selected option (optional override)
  @Input() public defaultOption: DateRangeFilter | null = null;

  // Optional external form control for reactive forms
  @Input() public formControls!: AbstractControl | null;

  // Emits selected value changes for external listeners
  @Output() public valueChange = new EventEmitter<Date | [Date, Date] | null>();

  // Available enum options
  public dateRangeFilterEnum = DateRangeFilter;

  // Mapped options for dropdown UI - will be generated dynamically
  public dateRangeFilterList: string[] = [];

  // Currently selected option
  public selectedOption: DateRangeFilter = DateRangeFilter.LAST_7_DAYS;

  // Bound model value (emitted to form — may be expanded range for Before/After)
  public value: Date | [Date, Date] | null = null;

  // Display-only value bound to the date input (raw user selection)
  public displayValue: Date | [Date, Date] | null = null;

  // Placeholder CVA handlers (overridden via register methods)
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onChange: (val: Date | [Date, Date] | null) => void = () => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  public onTouched: () => void = () => {};

  ngOnInit() {
    // Initialize the filter list based on input or default
    this._initializeFilterList();

    // Use defaultOption if provided
    if (this.defaultOption) {
      this.selectedOption = this.defaultOption;
    }

    // Set initial option
    this.onOptionSelect(this.selectedOption);
  }

  /**
   * CVA: Invoked when form value is set externally.
   */
  public writeValue(val: Date | [Date, Date] | null): void {
    this.value = val;
  }

  /**
   * CVA: Registers change handler for form integration.
   */
  public registerOnChange(fn: (val: Date | [Date, Date] | null) => void): void {
    this.onChange = fn;
  }

  /**
   * CVA: Registers touched handler for form integration.
   */
  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Whether to show the custom date picker input based on selected option.
   */
  public get showCustomPicker(): boolean {
    return [
      DateRangeFilter.BETWEEN,
      DateRangeFilter.AFTER,
      DateRangeFilter.BEFORE,
    ].includes(this.selectedOption);
  }

  /**
   * Called when a predefined option is selected.
   * Computes date range accordingly and sets value.
   */
  public onOptionSelect(option: DateRangeFilter): void {
    this.selectedOption = option;
    this.displayValue = null;
    this._setValue(this._computeRange(option));
  }

  /**
   * Selects an option and refreshes the range WITHOUT emitting `valueChange`.
   *
   * Used when a report's date window was widened server-side (`autoFallback`): the data already on
   * screen covers the new range, so only the dropdown has to catch up. Going through
   * `onOptionSelect` here would emit, trigger another fetch, and loop.
   */
  public applySelectionSilently(option: DateRangeFilter): void {
    this.selectedOption = option;
    this.displayValue = null;
    this.value = this._computeRange(option);
  }

  /**
   * Handles user-selected custom dates for BETWEEN/BEFORE/AFTER filters.
   */
  public onCustomDateSelection(val: Date | Date[] | null): void {
    const isDate = val instanceof Date;
    const isRange =
      Array.isArray(val) && val[0] instanceof Date && val[1] instanceof Date;

    // Boundaries for BEFORE/AFTER ranges
    const today = new Date();
    const hundredYearsAgo = new Date(today);
    hundredYearsAgo.setFullYear(hundredYearsAgo.getFullYear() - 100);

    const hundredYearsAhead = new Date(today);
    hundredYearsAhead.setFullYear(hundredYearsAhead.getFullYear() + 100);

    switch (this.selectedOption) {
      case DateRangeFilter.BETWEEN:
        if (!isRange) {
          return;
        }
        this.displayValue = val as [Date, Date];
        this._setValue(val as [Date, Date]);
        break;

      case DateRangeFilter.BEFORE:
        if (!isDate) {
          return;
        }
        this.displayValue = val as Date;
        this._setValue([hundredYearsAgo, val]);
        break;

      case DateRangeFilter.AFTER:
        if (!isDate) {
          return;
        }
        this.displayValue = val as Date;
        this._setValue([val, hundredYearsAhead]);
        break;

      default:
        this._setValue(null);
    }
  }

  /**
   * Resolves a predefined option to its date range. Pure — no state changes, no emits.
   */
  private _computeRange(option: DateRangeFilter): Date | [Date, Date] | null {
    const today = new Date();
    const cleanToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    );

    let result: Date | [Date, Date] | null = null;

    switch (option) {
      case DateRangeFilter.TODAY: {
        // Start and end are both today
        const start = new Date(cleanToday);
        const end = new Date(cleanToday);
        result = [start, end];
        break;
      }

      case DateRangeFilter.YESTERDAY: {
        const date = new Date(cleanToday);
        date.setDate(date.getDate() - 1);
        result = [date, date];
        break;
      }

      case DateRangeFilter.LAST_WEEK: {
        // Last week Monday to Sunday
        const end = new Date(cleanToday);
        end.setDate(end.getDate() - end.getDay()); // Sunday
        const start = new Date(end);
        start.setDate(end.getDate() - 6); // Monday
        result = [start, end];
        break;
      }

      case DateRangeFilter.LAST_MONTH: {
        // First to last day of previous month
        const start = new Date(
          cleanToday.getFullYear(),
          cleanToday.getMonth() - 1,
          1
        );
        const end = new Date(
          cleanToday.getFullYear(),
          cleanToday.getMonth(),
          0
        );
        result = [start, end];
        break;
      }

      case DateRangeFilter.THIS_MONTH: {
        const start = new Date(
          cleanToday.getFullYear(),
          cleanToday.getMonth(),
          1
        );
        const end = new Date(cleanToday);
        result = [start, end];
        break;
      }

      // New date range options
      case DateRangeFilter.LAST_7_DAYS: {
        const end = new Date(cleanToday);
        end.setDate(end.getDate() - 1);
        const start = new Date(cleanToday);
        start.setDate(start.getDate() - 7);
        result = [start, end];
        break;
      }

      case DateRangeFilter.LAST_30_DAYS: {
        const end = new Date(cleanToday);
        end.setDate(end.getDate() - 1);
        const start = new Date(cleanToday);
        start.setDate(start.getDate() - 30);
        result = [start, end];
        break;
      }

      case DateRangeFilter.LAST_60_DAYS: {
        const end = new Date(cleanToday);
        end.setDate(end.getDate() - 1);
        const start = new Date(cleanToday);
        start.setDate(start.getDate() - 60);
        result = [start, end];
        break;
      }

      case DateRangeFilter.LAST_90_DAYS: {
        const end = new Date(cleanToday);
        end.setDate(end.getDate() - 1);
        const start = new Date(cleanToday);
        start.setDate(start.getDate() - 90);
        result = [start, end];
        break;
      }

      case DateRangeFilter.LAST_6_MONTHS: {
        const end = new Date(cleanToday);
        end.setDate(end.getDate() - 1);
        const start = new Date(cleanToday);
        start.setDate(start.getDate() - 1);
        start.setMonth(start.getMonth() - 6);
        result = [start, end];
        break;
      }

      case DateRangeFilter.LAST_12_MONTHS: {
        const end = new Date(cleanToday);
        end.setDate(end.getDate() - 1);
        const start = new Date(cleanToday);
        start.setDate(start.getDate() - 1);
        start.setMonth(start.getMonth() - 12);
        result = [start, end];
        break;
      }

      case DateRangeFilter.ALL:
      default:
        result = null;
        break;
    }

    return result;
  }

  /**
   * Centralized method to update internal state, emit changes and notify form.
   */
  private _setValue(val: Date | [Date, Date] | null): void {
    this.value = val;
    this.onChange(val);
    this.onTouched();
    this.valueChange.emit(val);
  }

  /**
   * Initialize the filter list based on input or default
   */
  private _initializeFilterList(): void {
    // Use provided filter options or default
    this.dateRangeFilterList = this.filterOptions.map((option) => option);
  }
}
